import React, { useCallback, useEffect, useState } from 'react';

import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import SectionMessage from '@atlaskit/section-message';
import useAllWebhooksValidationTasks from 'src/services/webhooks/useAllWebhooksValidationTasks';

import { trackEvent } from 'src/lib/analytics';
import integration from 'src/lib/api/integration';
import jira from 'src/lib/api/jira';

import WebhooksTasksTable from 'src/views/Admin/Modals/components/WebhooksTasksTable';
import CenteredSpinner from 'src/views/components/CenteredSpinner';
import ModalContainer from 'src/views/components/ModalContainer';

import ErrorMessage from '../../components/ErrorMessage';

const RemoveWebhooks = ({ isRunning, onDone }) => {
  const { error, loading, tasks } = useAllWebhooksValidationTasks({ actionType: 'delete', actionScope: 'both' });

  if (error) return <ErrorMessage error={['Failed to load webhooks', error]}/>;
  if (loading) return <CenteredSpinner size="large"/>;

  return (
    <div style={{ paddingLeft: 8 }}>
      <WebhooksTasksTable tasks={tasks} isRunning={isRunning} onDone={onDone}/>
    </div>
  );
};

const ConfirmAccountsMappingRemove = () => {
  const [removing, setRemoving] = useState(false);
  const [removeWebhooks, setRemoveWebhooks] = useState(false);
  const [removeWebhooksInProgress, setRemoveWebhooksInProgress] = useState(false);
  const [removeWebhooksStatus, setRemoveWebhooksStatus] = useState({ hasErrors: undefined });
  const [removingError, setRemovingError] = useState(undefined);

  const removeAccountsMapping = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const accountsMappingId = searchParams.get('accountsMappingId');

    setRemoving(true);
    integration.removeAccountsMapping(accountsMappingId)
      .then(() => {
        jira.closeCurrentModal();
        trackEvent('settings_integration', { label: 'disconnect_account' });
      })
      .catch((e) => setRemovingError(e))
      .then(() => setRemoving(false));
  }, []);

  const remove = (e) => {
    setRemoving(true);
    if (removeWebhooks) {
      setRemoveWebhooksInProgress(true);
    } else {
      removeAccountsMapping();
    }
  };

  useEffect(() => {
    if (removeWebhooksStatus.hasErrors === false) {
      removeAccountsMapping();
    } else if (removeWebhooksStatus.hasErrors === true) {
      setRemoving(false);
    }
  }, [removeAccountsMapping, removeWebhooksStatus]);

  return (
    <ModalContainer title="Confirm Accounts mapping removal">
      <SectionMessage appearance="warning" title="Accounts mapping and all configuration will be removed">
        {!removeWebhooks && <span>
        We will remove:
          <ul>
            <li>All credentials used to access Jira and PagerDuty</li>
            <li>Rules</li>
            <li>User mapping</li>
            <li>Ability to work with integration from the sidebar for logged-out users</li>
          </ul>
        We leave:
          <ul>
            <li>Issue-Incident mappings stored in Issue props</li>
            <li>Ability to work with integration from the sidebar for logged-in users</li>
          </ul>
        </span>}
      </SectionMessage>
      <br/>
      <span style={{ fontWeight: 'bold' }}>
        <Checkbox
          isChecked={removeWebhooks}
          label="Also remove webhooks"
          onChange={() => setRemoveWebhooks(w => !w)}
          isDisabled={removeWebhooksInProgress || removing || removeWebhooksStatus.hasErrors !== undefined || removingError}
        />
      </span>
      {removeWebhooks && <RemoveWebhooks onDone={setRemoveWebhooksStatus} isRunning={removeWebhooksInProgress}/>}
      {removeWebhooksStatus.hasErrors && <SectionMessage appearance="warning" title="Failed to remove some webhooks"/>}
      {removingError && <ErrorMessage error={['Failed to remove accounts mapping', removingError]}/>}
      <br/>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ButtonGroup>
          <Button onClick={e => jira.closeCurrentModal()}>Cancel</Button>
          {removeWebhooksStatus.hasErrors !== true && <LoadingButton appearance="danger" onClick={remove} isLoading={removing} isDisabled={Boolean(removingError)}>Remove Account Integration</LoadingButton>}
          {removeWebhooksStatus.hasErrors === true && <LoadingButton appearance="danger" onClick={removeAccountsMapping} isLoading={removing} isDisabled={Boolean(removingError)}>Remove Account Integration anyway</LoadingButton>}
        </ButtonGroup>
      </div>
      <br/>
      <br/>
    </ModalContainer>
  );
};

export default ConfirmAccountsMappingRemove;
